<script setup lang="ts">
    import { CellContentAlignment } from "@/components/KTable/helpers";
    import KCell from "@/components/KTable/KCell.vue";
    import { useStore } from "@/store";
    import DropdownSelector from "@/tabs/Operation/DropdownSelector.vue";
    import { getErrorMessage } from "@/api";
    import { statusar } from "@/tabs/Operation/utils";
    import { anmalanStatusIcons } from "@/tabs/Operation/utilsTs";
    import { flashMessage, openDialog } from "@/utils";
    import { computed, ref, toRefs, watch } from "vue";

    const store = useStore();

    const props = defineProps<{
        data: unknown;
    }>();

    const { data } = toRefs(
        props as {
            data: {
                status: string;
                id: string;
                huvudoperator: string | null;
            };
        }
    );

    const canChangeStatus = computed(() => {
        return store.getters["ability/can"]({ action: "changestatus", subject: "anmalan" });
    });

    // Endast dessa statusar ska vara valbara i dropdownen.
    const selectableStatusValues = new Set([
        "Väntar på preanestesibedömning",
        "Prioriterad preanestesibedömning",
        "Klar för operationsbokning",
        "Väntar på hälsodeklaration",
        "Offertförfrågan skickad",
        "Betalningsförbindelse skickad",
        "Offertförfrågan ej skickad",
        "Väntar på viktnedgång",
        "Väntar på rökstopp",
        "Väntar på prover/undersökning",
    ]);

    // Blockera statusar som inte ar tillatna i nuvarande anmalan-state.
    const isStatusDisabled = (statusValue: string, isBaseDisabled: boolean) => {
        if (isBaseDisabled || !selectableStatusValues.has(statusValue)) {
            return true;
        }

        if (statusValue === "Klar för operationsbokning" && !data.value.huvudoperator) {
            return true;
        }

        return false;
    };

    const options = computed(() =>
        statusar.map((status) => ({
            label: status.label,
            value: status.value,
            icon: anmalanStatusIcons[status.value as keyof typeof anmalanStatusIcons],
            disabled: isStatusDisabled(status.value, status.disabled),
        }))
    );

    const isEditable = (initialValue?: string): boolean =>
        canChangeStatus.value && (!initialValue || !["Bokad", "Pågående", "Struken", "Skickad"].includes(initialValue));

    // Visa felmeddelande fran backend, och markera valideringsfel tydligt.
    const getStatusErrorMessage = (error: unknown) => {
        const baseMessage = getErrorMessage(error).trim();
        if (!baseMessage) {
            return "Ändringar av status kunde inte sparas.";
        }

        const cleanedMessage = baseMessage.replace(/(^|[\n;]\s*)[^:\n;]+:\s*/g, "$1").trim();
        const finalMessage = cleanedMessage || baseMessage;

        return finalMessage;
    };

    const onValueChange = async ({ newValue, isChanged }: { newValue: string; isChanged: boolean }) => {
        if (!isChanged || newValue === data.value.status) {
            return;
        }

        try {
            await store.dispatch("operation/operationsanmalanV2/partialUpdateOperationsanmalan", {
                operationsanmalan: {
                    id: data.value.id,
                    status: newValue,
                    huvudoperator: data.value.huvudoperator,
                },
                freezeAllSubsets: true,
            });

            store.commit(
                "operation/operationsanmalan/setOpAnmala",
                store.getters["operation/operationsanmalanV2/operationsanmalanById"]({ id: data.value.id })
            );
            flashMessage(`Status ändrad till ${newValue}`);
        } catch (error) {
            console.error(error);
            openDialog(getStatusErrorMessage(error), "error");
        }
    };
</script>

<template>
    <KCell class="cell-status" :alignContent="CellContentAlignment.Center">
        <template #content>
            <DropdownSelector
                :initialValue="data.status"
                :options="options"
                :deferSelection="true"
                :editable="isEditable"
                :icons="anmalanStatusIcons"
                optionLabel="label"
                optionValue="value"
                dropdownTitel="Ändra status"
                @statusChanged="onValueChange"
            />
        </template>
    </KCell>
</template>

<style scoped lang="scss">
    .cell-status {
    }
</style>
